import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../lib/axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar, Clock, Users, Activity, Building2, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';
import { formatTime12h } from '../lib/utils';

export default function DoctorDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [doctorProfile, setDoctorProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, appointmentsRes, profileRes] = await Promise.all([
          axiosInstance.get('/dashboard/stats'),
          axiosInstance.get('/appointments'),
          axiosInstance.get('/doctors/me')
        ]);
        
        setStats(statsRes.data);
        setAppointments(appointmentsRes.data);
        setDoctorProfile(profileRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleStatusUpdate = async (appointmentId, newStatus) => {
    try {
      await axiosInstance.patch(`/appointments/${appointmentId}/status?status=${newStatus}`);
      toast.success('Appointment status updated');
      
      // Refresh appointments
      const res = await axiosInstance.get('/appointments');
      setAppointments(res.data);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'pending': return 'bg-amber-50 text-amber-700 border-amber-100';
      case 'completed': return 'bg-blue-50 text-blue-700 border-blue-100';
      case 'cancelled': return 'bg-red-50 text-red-700 border-red-100';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="h-12 w-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-manrope font-bold mb-2" data-testid="doctor-dashboard-title">
            Welcome, Dr. {user?.full_name}
          </h1>
          <p className="text-slate-600">Your medical practice overview</p>
        </div>

        {/* Doctor Profile Card */}
        {doctorProfile && (
          <Card className="mb-8 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20" data-testid="doctor-profile-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Stethoscope className="h-5 w-5 text-primary" />
                Your Profile
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Specialization</p>
                  <p className="font-medium">{doctorProfile.doctor?.specialization}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Department</p>
                  <p className="font-medium">{doctorProfile.department?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Hospital</p>
                  <p className="font-medium">{doctorProfile.hospital?.name}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card data-testid="stat-total-appointments">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Total Appointments</CardDescription>
                  <Calendar className="h-4 w-4 text-slate-400" />
                </div>
                <CardTitle className="text-3xl">{stats.total_appointments || 0}</CardTitle>
              </CardHeader>
            </Card>
            
            <Card data-testid="stat-upcoming-appointments">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Upcoming</CardDescription>
                  <Clock className="h-4 w-4 text-primary" />
                </div>
                <CardTitle className="text-3xl text-primary">{stats.upcoming_appointments || 0}</CardTitle>
              </CardHeader>
            </Card>

            <Card data-testid="stat-total-patients">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardDescription>Unique Patients</CardDescription>
                  <Users className="h-4 w-4 text-slate-400" />
                </div>
                <CardTitle className="text-3xl">{stats.total_patients || 0}</CardTitle>
              </CardHeader>
            </Card>
          </div>
        )}

        {/* Appointments List */}
        <Card data-testid="doctor-appointments-list">
          <CardHeader>
            <CardTitle>Your Appointments</CardTitle>
            <CardDescription>
              {appointments.length === 0 ? 'No appointments scheduled' : `${appointments.length} appointment(s)`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {appointments.map((apt, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid={`doctor-appointment-${index}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge className={getStatusColor(apt.status)}>
                        {apt.status}
                      </Badge>
                      <span className="text-sm text-slate-500">Patient: {apt.patient_email}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {apt.appointment_date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatTime12h(apt.appointment_time)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {apt.consultation_type}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    {apt.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleStatusUpdate(apt.id, 'confirmed')}
                          data-testid={`confirm-apt-${index}`}
                        >
                          Confirm
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStatusUpdate(apt.id, 'cancelled')}
                          data-testid={`cancel-apt-${index}`}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                    {apt.status === 'confirmed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStatusUpdate(apt.id, 'completed')}
                        data-testid={`complete-apt-${index}`}
                      >
                        Mark Complete
                      </Button>
                    )}
                    {apt.google_meet_link && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(apt.google_meet_link, '_blank')}
                        data-testid={`join-video-${index}`}
                      >
                        Join Video
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
